<!-- Copyright 2026, Common Good Learning Tools LLC -->
<template><k-dialog v-model="dialog_open" fullscreen persistent scrollable><k-card>
<k-card-title class="k-rcram-title"
	:style="`background-color:${report_banner_bgd_color}`"
>
	<img class="k-rcram-logo" 
		:style="report_logo_style"
		:src="report_logo"
	>
	<div class="k-rcram-report-title"
		v-html="report_title"
	></div>

	<k-spacer/>

	<!-- not sure what this was for... -->
	<k-btn v-if="!opened_from_vapp && !cram_job_id" color="primary" @click="$emit('dialog_cancel')">Done</k-btn>

	<k-btn color="#666" dark fab small accessible-label="Close resource alignment tool" style="order:1"
		v-if="opened_from_vapp"
		@click="$emit('dialog_cancel')"
	><k-icon>fas fa-arrow-left</k-icon></k-btn>

</k-card-title>
<k-card-text class="k-rcram-body">
	<div class="k-rcram-h1">
		<div v-if="align_job" style="font-size:16px">Resource Framework:  </div>
		<div v-if="crosswalk_job" style="font-size:16px">Comp. A Framework:  </div>
		<a :href="resource_framework_href" target="_blank"><b v-html="resource_framework_title"></b></a>

		<k-spacer/>
		
		<k-btn x-small fab dark class="k-tight-btn elevation-0" style="margin-top:-6px"
			v-if="user_has_edit_rights"
			:color="!editing_enabled?'#ccc':'green darken-3'"
			@click="editing_enabled=!editing_enabled"
		><k-icon>fas fa-edit</k-icon>
		</k-btn>
		
		<k-btn x-small fab dark class="k-tight-btn elevation-0 ml-3" style="margin-top:-6px"
			v-if="user_has_edit_rights"
			:color="!job_stats_showing?'#ccc':'green darken-3'"
			@click="job_stats_showing=!job_stats_showing"
		><k-icon>fas fa-chart-simple</k-icon>
		</k-btn>
		
		<k-spacer/>
		
		<div v-if="align_job" style="font-size:16px">Comp. Framework:  </div>
		<div v-if="crosswalk_job" style="font-size:16px">Comp. B Framework:  </div>
		<a :href="comp_framework_href" target="_blank"><b v-html="comp_framework_title"></b></a>
	</div>

	<div class="mx-auto mb-3 mt-2" style="width:1000px"
		v-if="job_stats_showing"
	>
		<SCRAMJobStats 
			:v-if="job_status && matrix"
			:job_status_prop="job_status" 
			:matrix_prop="matrix"
			@edit_resources="edit_resources"
			@edit_comps="edit_comps"
		/>
	</div>

	<!-- ========================================================= -->
	<!-- -------------- GRID VIEW -------------------------------- -->
	<div class="k-rcram-table-outer elevation-5" v-if="initialized && dim=='grid'">
		<k-data-table light dense
			:headers="grid_headers"
			hide-default-header
			:items="grid_table_rows"
			:items-per-page.sync="table_items_per_page"
			:footer-props="{ 'items-per-page-options': footer_items_per_page_options }"
			:hide-default-footer="!show_default_footer"
			:height="show_default_footer ? 'calc(100vh - 172px)' : 'calc(100vh - 124px)'"
			class="k-rcram-table"
			:class="`k-rcram-table-${grid_mode}`"
		>
			<!-- HEADER ROWS -->
			<template v-slot:header><thead>
				<tr class="k-rcram-table-header">
					<th class="k-rcram-table-comp-header-cell"
						rowspan="2"
						:width="(grid_mode == 'axb') ? 340 : 600"
					>
						<div class="d-flex"
							style="justify-content:space-between; flex-direction:column; height:100%;"
						>
							<div class="pt-2 pb-3">
								<k-select dense outlined hide-details
									label="Grid Mode"
									:items="grid_mode_items"
									v-model="grid_mode"
								></k-select>
							</div>

							<div class="pb-2" style="font-size:18px!important; line-height:21px!important">
								<b style="text-transform:uppercase;">{{grid_row_label}}</b>
							</div>
						</div>
					</th>

					<th class="text-left py-3 pl-8"
						style="font-size:18px!important"
						:colspan="n_grid_columns" 
					><b style="text-transform: uppercase;">{{grid_column_label}}</b></th>
				</tr>

				<tr class="k-rcram-table-header">
					<th v-for="(header, i) in grid_headers"
						v-if="i > 0"
						:key="header.cell_id"
						:id="`th-${header.cell_id}`"
						:width="(grid_mode == 'axb') ? 100 : 80"
						:class="(grid_table_cell_class('', header.cell_id) + ' k-rcram-table-resource-header-cell text-center')"
						@click="last_viewed_item = null"
					>
						<!-- Resource header cells -->
						<div class="k-rcram-resource-header-div"
						>
							<div v-html="header.text"></div>
							<div class="k-rcram-resource-header-btns"
								v-if="grid_mode == 'axb'"
							>
								<!-- button to show details for this resource -->
								<k-tooltip bottom><template v-slot:activator="{on}">
									<k-btn v-on="on" x-small color="#333" outlined class="ml-1"
										@click.stop="view_resource(header.cell_id)"
									><k-icon small color="#333">fas fa-rectangle-list</k-icon></k-btn>
								</template>List View for this {{resource_label}}</k-tooltip>

							</div>
						</div>
					</th>
				</tr>
			</thead></template>

			<!-- BODY ROWS -->
	 		<template v-slot:item="{ item }"><tr>
				<!-- Competency header column -->
				<td class="k-rcram-table-comp-header-cell" width="340"
					:class="grid_table_cell_class(item.row_id, '')"
					:id="`th-${item.row_id}`"
					@click="last_viewed_item = null"
				>
					<div v-html="item.row_text"></div>
					<div class="k-rcram-comp-header-btns"
						v-if="true || grid_mode == 'axb'"
					>
						<!-- button to show details for this comp -->
						<k-tooltip bottom><template v-slot:activator="{on}">
							<k-btn v-on="on" x-small color="#333" outlined class="mt-1"
								@click.stop="view_row_item(item.row_id)"
							><k-icon small color="#333">fas fa-fw fa-rectangle-list</k-icon></k-btn>
						</template>List View for this {{comp_label}}</k-tooltip>

					</div>
				</td>

				<!-- simscore columns -->
				<td v-for="(column) in item.columns"
					:key="column.cell_id"
					class="k-rcram-table-simscore-cell"
					:class="`${grid_table_cell_class(item.row_id, column.cell_id, column.cell)} ${column.cat.cell_class}`"
					@click="cell_clicked(column.cell, $event)"
				>
					<k-tooltip bottom
						open-delay="0"
						:disabled="!user_has_edit_rights"
					><template v-slot:activator="{on}">
						<k-icon v-on="on">{{column.cat.icon}}</k-icon>
					</template>
						<div style="max-width:640px">
							<div class="text-left mb-2" 
								v-html="column.cell_text"
								style="font-size:12px; line-height:15px"
							></div>
							<div v-if="editing_enabled" class="text-center" v-html="sme_toggle_tooltip"></div>
							<div v-else class="text-center">Enable editing to toggle SME {{crosswalk_job ? 'association' : 'alignment'}}</div>
						</div>
					</k-tooltip>
				</td>
			</tr></template>

		</k-data-table>
	</div>
	
	<!-- ========================================================= -->
	<!-- -------------- LIST OF RESOURCES FOR ONE COMPETENCY -------------------------- -->
	<div class="k-rcram-table-outer pa-3 elevation-5" v-if="initialized && dim=='comp'">
		<div class="mb-3 text-center">
			<k-btn small color="primary"
				@click="view_grid"
			><k-icon small class="mr-2">fas fa-table-cells</k-icon>Show Grid View</k-btn>

			<k-tooltip bottom><template v-slot:activator="{on}">
				<k-btn v-on="on" small fab color="primary" class="ml-4"
					v-visible="prev_comp_id"
					@click="view_comp(prev_comp_id)"
				><k-icon>fas fa-arrow-left</k-icon></k-btn>
			</template>Previous {{comp_label}}</k-tooltip>

			<k-tooltip bottom><template v-slot:activator="{on}">
				<k-btn v-on="on" small fab color="primary" class="ml-2"
					v-visible="next_comp_id"
					@click="view_comp(next_comp_id)"
				><k-icon>fas fa-arrow-right</k-icon></k-btn>
			</template>Next {{comp_label}}</k-tooltip>
		</div>

		<div class="mb-2 d-flex align-start k-rcram-list-header">
			<div class="mr-3"><b>{{comp_label}}:</b></div>
			<div v-html="cfitem_title(display_item)"></div>
		</div>

		<div class="mb-3 d-flex align-center">
			<div class="mr-10"><b>Top Alignment Suggestions</b></div>

			<div class="mr-2"
				v-if="sub_matrix"
			>Show:</div>
			<div style="width:200px"
				v-if="sub_matrix"
			>
				<k-select dense outlined hide-details
					:items="resource_level_options" 
					v-model="resource_level_to_show_local" 
				></k-select>
			</div>

			<k-spacer/>

			<div style="width:400px">
				<k-text-field dense outlined hide-details label="" placeholder="Search"
					prepend-inner-icon="fa fa-search" prepend-decorative clearable clear-icon="fa fa-times-circle"
					v-model="search_terms"
				></k-text-field>
			</div>
		</div>

		<!-- List item for each top-level resource (i.e. Chapter) -->
		<div class="k-cram-list-item-outer"
			v-for="(row, i) in comp_list_rows" :key="row.id"
			v-if="i < list_n_showing"
		>

			<div class="k-cram-list-item-header d-flex align-center"
				:class="row.cat.cell_class"
			>
				<div class="k-cram-list-cat-icon"
					:class="sme_icon_class(row.cell)"
					@click="cell_clicked(row.cell, $event)"
				>
					<k-tooltip right
						open-delay="200"
						:disabled="!user_has_edit_rights || hide_tooltips"
					><template v-slot:activator="{on}">
						<k-icon v-on="on">{{row.cat.icon}}</k-icon>
					</template>
						<div v-if="!editing_enabled">Similarity Score: <b>{{row.simscore}}</b></div>
						<div v-else class="text-center" v-html="sme_toggle_tooltip"></div>
					</k-tooltip>
				</div>
				<div v-if="simscores_showing" class="k-cram-list-item-simscore">{{row.simscore_display}}</div>
				<div class="k-cram-list-item-type">{{row.cfi.CFItemType}}</div>
				<div v-html="cfitem_title(row.cfi)"></div>

				<k-btn x-small text color="primary" class="ml-2 k-tight-btn" style="margin-top:3px;"
					v-if="item_description(row.cfi)"
					@click="toggle_description(row.id)"
				>
					{{descriptions_showing[row.id]?'Hide':'Show'}} {{description_label}}
					<k-icon small class="ml-1" style="margin-top:-6px;">fas fa-fw fa-sort-down</k-icon>
				</k-btn>
				
				<k-btn x-small text color="primary" class="ml-1"
					v-if="resource_aggregation_type && resource_level_to_show=='l0'"
					@click="view_resource(row.id)"
				><k-icon small>fas fa-fw fa-rectangle-list</k-icon></k-btn>

				<k-spacer/>
			</div>

			<div class="k-cram-resource-description ml-10 mt-1 mb-2"
				v-if="descriptions_showing[row.id]" 
				v-html="U.marked_latex(item_description(row.cfi))"
			></div>


			<!-- Controls for sub-resources -->
			<div class="my-2 ml-3 d-flex align-center"
				v-if="sub_matrix && resource_level_to_show=='l0'"
			>
				<k-btn outlined small color="primary" class="k-tight-btn mr-4"
					v-if="row.subrows.length > 0"
					@click="toggle_components_for(row.id)"
				>{{components_showing[row.id] ? 'Hide' : 'Show'}} {{sub_resource_label_plural}}</k-btn>

				<div v-if="components_showing[row.id]" class="d-flex align-center">
					<div class="mr-2">Sort by:</div>
					<div style="width:200px">
						<k-select dense outlined hide-details
							:items="list_view_sort_by_options" 
							v-model="resource_subrow_sort_by" 
						></k-select>
					</div>
				</div>

			</div>

			<!-- List items for each SUB-RESOURCE (i.e. Section) -->
			<div v-if="resource_level_to_show=='l0' && components_showing[row.id]">
				<div v-for="(subrow, i) in row.subrows" :key="subrow.id" class="my-2 mx-8">
					<div class="k-cram-list-item-header d-flex align-center"
						:class="subrow.cat.cell_class"
					>
						<div class="k-cram-list-cat-icon"
							@click="cell_clicked(row.cell, $event)"
							:class="sme_icon_class(subrow.cell)"
						>
							<k-tooltip right
								open-delay="200"
								:disabled="!user_has_edit_rights || hide_tooltips"
							><template v-slot:activator="{on}">
								<k-icon v-on="on">{{subrow.cat.icon}}</k-icon>
							</template>
								<div v-if="!editing_enabled">Similarity Score: <b>{{subrow.simscore}}</b></div>
								<div v-else class="text-center" v-html="sme_toggle_tooltip"></div>
							</k-tooltip>
						</div>
						<div v-if="simscores_showing" class="k-cram-list-item-simscore">{{subrow.simscore_display}}</div>
						<div class="k-cram-list-item-type">{{subrow.cfi.CFItemType}}</div>
						<div v-html="cfitem_title(subrow.cfi)"></div>

						<k-btn x-small text color="primary" class="ml-2 k-tight-btn" style="margin-top:3px;"
							@click="toggle_description(subrow.id)"
						>
							{{descriptions_showing[subrow.id]?'Hide':'Show'}} {{description_label}}
							<k-icon small class="ml-1" style="margin-top:-6px;">fas fa-fw fa-sort-down</k-icon>
						</k-btn>

						<k-spacer/>
					</div>

					<div class="k-cram-resource-description ml-10 mt-1 mb-4"
						v-if="descriptions_showing[subrow.id]" 
						v-html="U.marked_latex(item_description(subrow.cfi))"
					></div>
				</div>
			</div>
		</div>

		<div class="text-center"
			v-if="list_show_show_more_btn"
		>
			<k-btn small color="#ccc"
				@click="show_more"
			>Show More (+{{comp_list_rows.length - list_n_showing}})</k-btn>
		</div>
	</div>

	<!-- ========================================================= -->
	<!-- -------------- LIST OF COMPETENCIES FOR ONE RESOURCE --------------------------- -->
	<div class="k-rcram-table-outer pa-3 elevation-5" v-if="initialized && dim=='resource'">
		<div class="mb-3 text-center">
			<k-btn small color="primary"
				@click="view_grid"
			><k-icon small class="mr-2">fas fa-table-cells</k-icon>Return to Grid View</k-btn>

			<k-tooltip bottom><template v-slot:activator="{on}">
				<k-btn v-on="on" small fab color="primary" class="ml-4"
					v-visible="prev_resource_id"
					@click="view_resource(prev_resource_id)"
				><k-icon>fas fa-arrow-left</k-icon></k-btn>
			</template>Previous {{resource_label}}</k-tooltip>

			<k-tooltip bottom><template v-slot:activator="{on}">
				<k-btn v-on="on" small fab color="primary" class="ml-2"
					v-visible="next_resource_id"
					@click="view_resource(next_resource_id)"
				><k-icon>fas fa-arrow-right</k-icon></k-btn>
			</template>Next {{resource_label}}</k-tooltip>
		</div>

		<div class="mb-2 d-flex align-start k-rcram-list-header">
			<div class="mr-3"><b>{{resource_label}}:</b></div>
			<div v-html="cfitem_title(display_item)"></div>
			<k-btn x-small text color="primary" class="ml-2 k-tight-btn" style="margin-top:3px;"
				v-if="item_description(display_item)"
				@click="toggle_description(display_item.identifier)"
			>
				{{descriptions_showing[display_item.identifier]?'Hide':'Show'}} {{description_label}}
				<k-icon small class="ml-1" style="margin-top:-6px;">fas fa-fw fa-sort-down</k-icon>
			</k-btn>

		</div>

		<div class="k-cram-resource-description ml-10 mt-1 mb-2"
			v-if="descriptions_showing[display_item.identifier]" 
			v-html="U.marked_latex(item_description(display_item))"
		></div>

		<div class="mb-3 d-flex align-center">
			<div class="mr-10"><b>Top Alignment Suggestions</b></div>

			<k-spacer/>

			<div style="width:400px">
				<k-text-field dense outlined hide-details label="" placeholder="Search"
					prepend-inner-icon="fa fa-search" prepend-decorative clearable clear-icon="fa fa-times-circle"
					v-model="search_terms"
				></k-text-field>
			</div>
		</div>

		<!-- List item for each top-level competency -->
		<div class="k-cram-list-item-outer"
			v-for="(row, i) in resource_list_rows" :key="row.id"
			v-if="i < list_n_showing"
		>

			<div class="k-cram-list-item-header d-flex align-center"
				:class="row.cat.cell_class"
			>
				<div class="k-cram-list-cat-icon"
					:class="sme_icon_class(row.cell)"
					@click="cell_clicked(row.cell, $event)"
				>
					<k-tooltip right
						open-delay="200"
						:disabled="!user_has_edit_rights || hide_tooltips"
					><template v-slot:activator="{on}">
						<k-icon v-on="on">{{row.cat.icon}}</k-icon>
					</template>
						<div v-if="!editing_enabled">Similarity Score: <b>{{row.simscore}}</b></div>
						<div v-else class="text-center" v-html="sme_toggle_tooltip"></div>
					</k-tooltip>
				</div>
				<div v-if="simscores_showing" class="k-cram-list-item-simscore">{{row.simscore_display}}</div>
				<div class="k-cram-list-item-type">{{row.cfi.CFItemType}}</div>
				<div v-html="cfitem_title(row.cfi)"></div>

				<k-btn x-small text color="primary" class="ml-1"
					v-if="resource_aggregation_type"
					@click="view_comp(row.id)"
				><k-icon small>fas fa-fw fa-rectangle-list</k-icon></k-btn>

				<k-spacer/>
			</div>
		</div>

		<div class="text-center"
			v-if="list_show_show_more_btn"
		>
			<k-btn small color="#ccc"
				@click="show_more"
			>Show More (+{{resource_list_rows.length - list_n_showing}})</k-btn>
		</div>
	</div>

	<!-- ========================================================= -->
	<!-- -------------- KEY -------------------------------------- -->
	<div v-if="key_showing" class="k-rcram-key elevation-3">
		<div class="text-center mb-1">
			<k-btn small outlined color="primary"
				@click="key_showing = false"
			><k-icon small class="mr-2">fas fa-key</k-icon> Hide Key</k-btn>
		</div>

		<div v-for="(cat, i) in recommendation_categories" class="k-rcram-key-entry"
			:class="cat.cell_class"
		>
			<span style="display:inline-block; width:30px; text-align:center">
				<k-icon small>{{cat.icon}}</k-icon>
			</span>
			<span v-html="cat.label"></span>
		</div>

		<div v-if="align_job">
			<div class="k-rcram-key-entry mt-1 pt-1" style="border-top:1px solid #000">
				<span class="k-rcram-simscore-sme-tagged k-rcram-simscore-sme-tagged-primary" 
					style="display:inline-block; width:30px; text-align:center"
				>
					<k-icon small>fas fw fa-circle-star</k-icon>
				</span>
				Tagged Primary Alignment
			</div>

			<div class="k-rcram-key-entry">
				<span class="k-rcram-simscore-sme-tagged" 
					style="display:inline-block; width:30px; text-align:center"
				>
					<k-icon small>fas fw fa-circle</k-icon>
				</span>
				Tagged Aligned
			</div>

			<div class="k-rcram-key-entry">
				<span class="k-rcram-simscore-sme-not-aligned" 
					style="display:inline-block; width:30px; text-align:center"
				>
					<k-icon small>fas fw fa-ban</k-icon>
				</span>
				Tagged <i>NOT</i> Aligned
			</div>
		</div>

		<div v-if="crosswalk_job">
			<div class="k-rcram-key-entry mt-1 pt-1" style="border-top:1px solid #000">
				<span class="k-rcram-simscore-sme-tagged-primary" 
					style="display:inline-block; width:30px; text-align:center"
				>
					<k-icon small>fas fw fa-circle</k-icon>
				</span>
				Tagged exactMatchOf
			</div>

			<div class="k-rcram-key-entry">
				<span class="k-rcram-simscore-sme-tagged-primary" 
					style="display:inline-block; width:30px; text-align:center"
				>
					<k-icon small color="blue darken-3">fas fw fa-circle-half-stroke</k-icon>
				</span>
				Tagged isRelatedTo
			</div>

			<div class="k-rcram-key-entry">
				<span class="k-rcram-simscore-sme-not-aligned" 
					style="display:inline-block; width:30px; text-align:center"
				>
					<k-icon small>fas fw fa-ban</k-icon>
				</span>
				Tagged <i>NOT</i> Associated
			</div>
		</div>

		<div style="position:absolute; right:0; top:0; width:16px; height:16px; cursor:pointer" @click="simscores_showing=!simscores_showing"></div>
	</div>

	<div v-if="!key_showing" class="k-rcram-key-btn">
		<k-btn small color="primary"
			@click="key_showing = true"
		><k-icon small class="mr-2">fas fa-key</k-icon> Show Key</k-btn>
	</div>

</k-card-text>
</k-card></k-dialog></template>

<script>
import { mapState, mapGetters } from 'vuex'
import SCRAMReportMixin from './SCRAMReportMixin.js'
import SCRAMJobStats from './SCRAMJobStats'

export default {
	mixins: [SCRAMReportMixin],
	components: { SCRAMJobStats },
	props: {
		// req: { type: String, required: true },
		cram_job_id: { type: String, required: false, default() { return '1001'} },
		dim: { type: String, required: false, default() { return 'grid'} },
		dim_id: { type: String, required: false, default() { return ''} },
		resource_level_to_show: { type: String, required: false, default() { return 'l0'} },
		query: { required: false, default() { return null } },
		opened_from_vapp: { required: false, default() { return false } },
		edit_at_start: { required: false, default() { return false } },
	},
	data() { return {
		dialog_open: true,

		last_viewed_item: null,
		n_showing: 0,
		components_showing: {},
		table_row_update: true,
		key_showing: true,
		job_stats_showing: false,
		simscores_showing: true,
		descriptions_showing: {},

		SHOW_INITIAL_BLOCK_SIZE: 4,
		SHOW_MORE_BLOCK_SIZE: 25,

		resource_subrow_sort_by: 'simscore',
		list_view_sort_by_options: [
			{ value: 'sequence', text: 'Order in Chapter' },
			{ value: 'simscore', text: 'Similarity Score' },
		],

		resource_level_to_show_local: 'l0',

		editing_enabled: false,
		cell_just_changed: null,

		max_rank_columns: 20,
		max_resources_for_grid_mode: 40,
		max_rows_for_no_footer: 80,

		// page size chosen via the data-table footer, and the options it offers
		footer_items_per_page: 50,
		footer_items_per_page_options: [25, 50, 100],

		hide_tooltips: false,

		search_terms: '',
	}},
	computed: {
		...mapState(['user_info']),
		...mapGetters([]),

		initialized() {
			return this.matrix && this.rfr && this.cfr
		},

		// grid mode is stored in the lst state so it persists between sessions
		grid_mode: {
			get() { 
				let m = this.$store.state.lst.scram_grid_mode
				// if we have more than max_resources_for_grid_mode resources, don't allow axb mode
				if (this.n_resources > this.max_resources_for_grid_mode && m == 'axb') {
					return 'axhigh'
				}
				return m 
			},
			set(val) { this.$store.commit('lst_set', ['scram_grid_mode', val]) }
		},

		// labels depend on whether this is an align job or a crosswalk job
		grid_mode_items() {
			let arr
			if (this.align_job) {
				arr = [
					{ value: 'axb', text: 'Resources x Comps' },
					{ value: 'axhigh', text: 'Resources x Highest Alignments' },
					{ value: 'bxhigh', text: 'Comps x Highest Alignments' },
				]
			} else {
				arr = [
					{ value: 'axb', text: 'Comp. A x Comp B' },
					{ value: 'axhigh', text: 'Comp. A x Closest Associations' },
					{ value: 'bxhigh', text: 'Comp. B x Closest Associations' },
				]
			}

			// if we have more than max_resources_for_grid_mode resources, don't allow axb mode
			if (this.n_resources > this.max_resources_for_grid_mode) {
				arr.shift()
			}
			return arr
		},

		show_default_footer() {
			// if we have too many comps or resources to comfortably show at once, show the default footer (regardless of whether we're viewing by comps or resources
			if (this.n_resources > this.max_rows_for_no_footer && this.n_comps > this.max_rows_for_no_footer) return false
			return true
		},

		// without the footer, show all rows (-1); with it, the footer controls the page size
		table_items_per_page: {
			get() {
				if (!this.show_default_footer) return -1
				return this.footer_items_per_page
			},
			set(val) {
				// ignore updates while the footer is hidden so the chosen size isn't overwritten
				if (!this.show_default_footer) return
				this.footer_items_per_page = val
			}
		},

		recommendation_categories() {
			if (this.align_job) {
				return [
					{
						index: 0,
						label: 'Highly Likely Alignment',
						threshold: 80,
						cell_class: 'k-rcram-table-simscore-cell-4',
						icon: 'fas fa-fw fa-circle'	// -dot
					},
					{
						index: 1,
						label: 'Likely Alignment',
						threshold: 50,
						cell_class: 'k-rcram-table-simscore-cell-3',
						icon: 'fas fa-fw fa-circle-half-stroke'
					},
					{
						index: 2,
						label: 'Possible Alignment',
						threshold: 35,
						cell_class: 'k-rcram-table-simscore-cell-2',
						icon: 'far fa-fw fa-circle'
					},
					{
						index: 3,
						label: 'Unlikely Alignment',
						threshold: 20,
						cell_class: 'k-rcram-table-simscore-cell-1',
						icon: 'far fa-fw fa-circle'
					},
					{
						index: 4,
						label: 'Highly Unlikely Alignment',
						threshold: 0,
						cell_class: 'k-rcram-table-simscore-cell-0',
						icon: 'far fa-fw fa-minus'
					},
				]

			} else {
				return [
					{
						index: 0,
						label: 'Identical <span style="font-size:12px">(exactMatchOf)</span>',
						threshold: 100,
						cell_class: 'k-rcram-table-simscore-cell-4',
						icon: 'fas fa-fw fa-circle-star'
					},
					{
						index: 1,
						label: 'Nearly Identical <span style="font-size:12px">(exactMatchOf)</span>',
						threshold: 95,
						cell_class: 'k-rcram-table-simscore-cell-4',
						icon: 'fas fa-fw fa-circle'
					},
					{
						index: 2,
						label: 'Closely Related <span style="font-size:12px">(isRelatedTo)</span>',
						threshold: 70,
						cell_class: 'k-rcram-table-simscore-cell-3',
						icon: 'fas fa-fw fa-circle-half-stroke'
					},
					{
						index: 3,
						label: 'Some Overlap',
						threshold: 40,
						cell_class: 'k-rcram-table-simscore-cell-2',
						icon: 'far fa-fw fa-circle'
					},
					{
						index: 4,
						label: 'Little to No Overlap',
						threshold: 0,
						cell_class: 'k-rcram-table-simscore-cell-0',
						icon: 'far fa-fw fa-minus'
					},
				]
			}
		},

		resource_ids() {
			if (!this.initialized) return []

			let matrix = this.matrix
			// if we're showing a list for a competency and resource_level_to_show is 1, use sub_matrix
			if (this.dim == 'comp' && this.resource_level_to_show == 'l1') {
				matrix = this.sub_matrix
			}

			let arr = []
			for (let comp_id in matrix) {
				for (let resource_id in matrix[comp_id]) {
					arr.push(resource_id)
				}
				break
			}

			// if we're viewing a competency, sorting the resource_ids here
			if (this.dim == 'comp') {
				// console.warn('update resource_ids')
				this.table_row_update = false
				setTimeout(x=>this.table_row_update = true, 0)

				let comp_id = this.dim_id
				arr.sort((a,b)=>{
					a = this.matrix[comp_id][a]
					b = this.matrix[comp_id][b]
					return this.sort_fn(a,b)
				})
			}

			return arr
		},
		n_resources() { return this.resource_ids.length },

		comp_ids() {
			if (!this.initialized) return []

			let arr = []
			for (let comp_id in this.matrix) {
				arr.push(comp_id)
			}

			// if we're viewing a resource, sorting the comp_ids here
			if (this.dim == 'resource') {
				// console.warn('update comp_ids')
				this.table_row_update = false
				setTimeout(x=>this.table_row_update = true, 0)

				let resource_id = this.dim_id
				arr.sort((a,b)=>{
					a = this.matrix[a][resource_id]
					b = this.matrix[b][resource_id]
					return this.sort_fn(a,b)
				})
			}

			return arr
		},
		n_comps() { return this.comp_ids.length },

		// label for what each row represents, depending on the grid_mode
		grid_row_label() {
			if (this.grid_mode == 'axb' || this.grid_mode == 'bxhigh') {
				return this.comp_framework_title
			} else {	// axhigh
				return this.resource_framework_title
			}
		},

		// label for what each column represents, depending on the grid mode
		grid_column_label() {
			if (this.grid_mode == 'axb') {
				return this.resource_framework_title

			} else if (this.align_job) {
				return 'Highest-Ranking Alignment'

			} else {	// crosswalk_mode
				return 'Highest-Ranking Association'
			}
		},

		n_grid_columns() {
			// in axb mode, we always show one column per resource
			if (this.grid_mode == 'axb') {
				return this.resource_ids.length

			} else {
				let n
				// for bxhigh mode, start with the number of resources
				if (this.grid_mode == 'bxhigh') {
					n = this.resource_ids.length

				// for axhigh mode, start with the number of comps
				} else {
					n = this.comp_ids.length
				}

				// set a ceiling on the number of columns to show for axhigh/bxhigh modes
				if (n > this.max_rank_columns) {
					n = this.max_rank_columns
				}
				return n
			}
		},

		// this establishes the columns for the grid, depending on the grid mode
		grid_headers() {
			let arr = []

			// the first column will be handled separately
			arr.push({
				text: '–',
				align: 'left',
				value: 'row_text',
			})
			
			// axb: columns are resources / comp_a's
			if (this.grid_mode == 'axb') {
				// one column per resource
				for (let resource_id of this.resource_ids) {
					let r = this.resource_cfitems[resource_id]
					arr.push({
						text: this.cfitem_title(r, 40),
						cell_id: resource_id,
						align: 'center',
						value: `simscore-${resource_id}`,
						cfi: r,
					})
				}

			// bxhigh / axhigh
			} else {
				// show the top n_grid_columns alignments/assocs
				for (let i = 0; i < this.n_grid_columns; ++i) {
					arr.push({
						text: (i + 1) + '',
						cell_id: i,
						align: 'center',
						value: `simscore-${i}`,
					})
				}
			}
			return arr
		},

		grid_table_rows() {
			if (!this.initialized) return []
			if (!this.table_row_update) return []

			// console.warn('update grid_table_rows: ' + this.table_row_update)
			let arr = []

			// axb: rows are comps / comp_b's; columns are resources / comp_a's
			if (this.grid_mode == 'axb') {
				// one row per comp
				let i = 0
				for (let comp_id of this.comp_ids) {
					let cfi = this.comp_cfitems[comp_id]
					++i

					// first cell is the comp itself; get that info
					let o = {
						row_id: comp_id,
						row_sequence: i,
						row_text: this.cfitem_title(cfi, 200),
						cfi: cfi,
						
						// this will hold the cells
						columns: []
					}

					for (let resource_id of this.resource_ids) {
						let cell = this.matrix[comp_id][resource_id]
						let cfi = this.resource_cfitems[resource_id]
						o[`simscore-${resource_id}`] = cell.simscore
						o.columns.push({
							cfi: cfi,
							resource_id: resource_id,
							cell_text: this.cfitem_title(cfi),
							cell: cell,
							simscore: cell.simscore,
							sme_alignment: cell.sme_alignment,
							cat: this.rco(cell)
						})
					}

					arr.push(o)
				}

			// bxhigh: rows are comps / comp_b's; columns are highest alignments/assocs for resources / comp_a items
			} else if (this.grid_mode == 'bxhigh') {
				// one row per comp
				let i = 0
				for (let comp_id of this.comp_ids) {
					let cfi = this.comp_cfitems[comp_id]
					++i

					// first cell is the comp itself; get that info
					let o = {
						row_id: comp_id,
						row_sequence: i,
						row_text: this.cfitem_title(cfi, null),	// no length limit
						cfi: cfi,
						
						// this will hold the columns
						columns: []
					}

					// create cols array and sort
					let cols = []
					for (let resource_id in this.matrix[comp_id]) {
						cols.push(this.matrix[comp_id][resource_id])
					}

					cols.sort((a,b)=>{
						return this.sort_fn(a,b)
					})

					for (let j = 0; j < this.n_grid_columns; ++j) {
						let cell = cols[j]
						let cell_id = this.align_job ? cell.resource_id : cell.competency_a_id
						let cfi = this.resource_cfitems[cell_id]
						o[`simscore-${cell_id}`] = cell.simscore
						o.columns.push({
							cfi: cfi,
							cell_id: cell_id,
							cell_text: this.cfitem_title(cfi),
							cell: cell,
							simscore: cell.simscore,

							// depending on job_type, one of the following two will always be blank; the other might be filled in
							sme_alignment: cell.sme_alignment,
							sme_assoc: cell.sme_assoc,

							cat: this.rco(cell)
						})
					}

					arr.push(o)
				}

			// axhigh: rows are resources / comp_a's; columns are highest alignments/assocs
			} else {
				// one row per resource
				let i = 0
				for (let resource_id of this.resource_ids) {
					let cfi = this.resource_cfitems[resource_id]
					++i

					// first cell is the comp itself; get that info
					let o = {
						row_id: resource_id,
						row_sequence: i,
						row_text: this.cfitem_title(cfi, null),	// no length limit
						cfi: cfi,
						
						// this will hold the columns
						columns: []
					}

					// create cols array and sort
					let cols = []
					for (let comp_id in this.matrix) {
						cols.push(this.matrix[comp_id][resource_id])
					}

					cols.sort((a,b)=>{
						return this.sort_fn(a,b)
					})

					for (let j = 0; j < this.n_grid_columns; ++j) {
						let cell = cols[j]
						let cell_id = this.align_job ? cell.competency_id : cell.competency_b_id
						let cfi = this.comp_cfitems[cell_id]
						o[`simscore-${cell_id}`] = cell.simscore
						o.columns.push({
							cfi: cfi,
							cell_id: cell_id,
							cell_text: this.cfitem_title(cfi),
							cell: cell,
							simscore: cell.simscore,

							// depending on job_type, one of the following two will always be blank; the other might be filled in
							sme_alignment: cell.sme_alignment,
							sme_assoc: cell.sme_assoc,

							cat: this.rco(cell)
						})
					}

					arr.push(o)
				}
			}

			return arr
		},

		// display item
		display_item() {
			let o = this.comp_cfitems[this.dim_id]
			if (!empty(o)) return o

			o = this.resource_cfitems[this.dim_id]
			if (!empty(o)) return o
		},

		// position of the displayed competency within the matrix's competency order (-1 if not found)
		comp_index() {
			return this.comp_ids.indexOf(this.dim_id)
		},

		// id of the competency before the displayed one in the matrix, or null if there isn't one
		prev_comp_id() {
			if (this.comp_index < 1) return null
			return this.comp_ids[this.comp_index - 1]
		},

		// id of the competency after the displayed one in the matrix, or null if there isn't one
		next_comp_id() {
			if (this.comp_index < 0) return null
			return this.comp_ids[this.comp_index + 1] ?? null
		},

		// position of the displayed resource within the matrix's resource order (-1 if not found)
		resource_index() {
			return this.resource_ids.indexOf(this.dim_id)
		},

		// id of the resource before the displayed one in the matrix, or null if there isn't one
		prev_resource_id() {
			if (this.resource_index < 1) return null
			return this.resource_ids[this.resource_index - 1]
		},

		// id of the resource after the displayed one in the matrix, or null if there isn't one
		next_resource_id() {
			if (this.resource_index < 0) return null
			return this.resource_ids[this.resource_index + 1] ?? null
		},

		// these are the rows we show when showing a list of resources for a specified competency
		comp_list_rows() {
			let arr = []

			let matrix = this.matrix
			if (!empty(this.sub_matrix) && this.resource_level_to_show == 'l1') {
				matrix = this.sub_matrix
			}

			// list of resources for a given competency
			for (let resource_id of this.resource_ids) {
				let cell = matrix[this.dim_id][resource_id]
				let cfi = this.resource_cfitems[resource_id]

				let o = {
					cfi: cfi,
					id: resource_id,
					cell: cell,
					simscore: cell.simscore,
					simscore_display: this.simscore_display(cell),
					cat: this.rco(cell),
				}

				// if we have search terms, limit by them
				if (!empty(this.search_terms)) {
					let st = this.search_terms.toLowerCase()
					let found = false
					if (cfi.fullStatement.toLowerCase().includes(st)) found = true
					else if (cfi.identifier.toLowerCase().includes(st)) found = true
					else if (cfi.humanCodingScheme && cfi.humanCodingScheme.toLowerCase().includes(st)) found = true

					// skip if search_terms not found in any of the above fields
					if (!found) continue
				}

				if (this.resource_level_to_show == 'l0') {
					// get "subrows" for this resource
					let subrows = []
					let children = cfi.tree_nodes[0]?.children
					if (children) {
						for (let child of children) {
							let sub_cell = this.sub_matrix[this.dim_id][child.cfitem.identifier]
							if (empty(sub_cell)) {
								console.warn(`couldn’t find sub_cell for ${this.dim_id} / ${child.cfitem.identifier}`)
							}

							// if we have search terms, limit by them
							if (!empty(this.search_terms)) {
								let st = this.search_terms.toLowerCase()
								let found = false
								if (child.cfitem.fullStatement.toLowerCase().includes(st)) found = true
								else if (child.cfitem.identifier.toLowerCase().includes(st)) found = true
								else if (child.cfitem.humanCodingScheme && child.cfitem.humanCodingScheme.toLowerCase().includes(st)) found = true

								// skip if search_terms not found in any of the above fields
								if (!found) continue
							}

							subrows.push({
								cfi: child.cfitem,
								id: child.cfitem.identifier,
								cell: sub_cell,
								simscore: sub_cell.simscore,
								simscore_display: this.simscore_display(sub_cell),
								cat: this.rco(sub_cell)
							})
						}

						// IFF resource_subrow_sort_by is 'simscore', sort subrows by simscore descending (otherwise we want to sort in child order, which is what we'll have if we don't sort)
						if (this.resource_subrow_sort_by == 'simscore') {
							subrows.sort((a,b) => b.simscore - a.simscore)
						}
					}
					o.subrows = subrows
				}

				arr.push(o)
			}

			return arr
		},

		// these are the list of rows we show when showing competencies for a given resource
		resource_list_rows() {
			let arr = []

			// list of competencies for a given resource
			for (let comp_id of this.comp_ids) {
				let cell = this.matrix[comp_id][this.dim_id]
				let cfi = this.comp_cfitems[comp_id]

				// if we have search terms, limit by them
				if (!empty(this.search_terms)) {
					let st = this.search_terms.toLowerCase()
					let found = false
					if (cfi.fullStatement.toLowerCase().includes(st)) found = true
					else if (cfi.identifier.toLowerCase().includes(st)) found = true
					else if (cfi.humanCodingScheme && cfi.humanCodingScheme.toLowerCase().includes(st)) found = true

					// skip if search_terms not found in any of the above fields
					if (!found) continue
				}

				arr.push({
					cfi: cfi,
					id: comp_id,
					cell: cell,
					simscore: cell.simscore,
					simscore_display: this.simscore_display(cell),
					cat: this.rco(cell),
				})
			}

			return arr
		},

		list_default_n_showing() {
			let n = 0
			let current_cat_index
			let arr = this.dim == 'comp' ? this.comp_list_rows : this.resource_list_rows
			for (let row of arr) {
				// always show at least SHOW_INITIAL_BLOCK_SIZE items
				if (n < this.SHOW_INITIAL_BLOCK_SIZE) { ++n; continue; }

				// always show likely alignments
				if (row.cat.index <= 1) { ++n; continue; }

				// if we get to here and current_cat_index isn't set, set to the this row's index
				if (empty(current_cat_index)) {
					current_cat_index = arr[n-1].cat.index
				}
				
				// show all items with current_cat_index, until we get to 0
				if (current_cat_index < 5 && row.cat.index == current_cat_index) { ++n; continue; }

				// break when we get to here
				break
			}
			return n
		},

		list_n_showing() {
			let arr = this.dim == 'comp' ? this.comp_list_rows : this.resource_list_rows
			if (this.n_showing == -1) {
				return this.arr.length

			} else if (this.n_showing == 0) {
				return this.list_default_n_showing

			} else {
				return this.n_showing
			}
		},

		list_show_show_more_btn() {
			let arr = this.dim == 'comp' ? this.comp_list_rows : this.resource_list_rows
			if (this.n_showing >= arr.length) return false
			return true
		},

		resource_level_options() {
			return [
				// TODO: make this a computed and use item types
				{ value: 'l0', text: this.resource_label_plural },
				{ value: 'l1', text: this.sub_resource_label_plural },
			]
		},

		report_title() {
			// TODO: store this as part of the job
			if (this.is_bju_report) return `<b>BJU Press</b> Educational Standards Correlation and Comparison Report: Grid View`
			return `<b>Rosetta Align:</b> Matrix View for SCRAM Job ID ${this.cram_job_id}`
			// return `<b>Satchel Rosetta Align</b> Standards Alignment Report: Grid View`
		},

		user_has_edit_rights() {
			// for now only allow edit rights for superusers
			return vapp.is_granted('super')
		},

		sme_toggle_tooltip() {
			let s
			if (this.align_job) {
				s = 'Click to toggle SME alignment<br>'
					+ `${U.meta_or_alt_key_symbol()}-click for primary / `
					+ `SHIFT-click for “not aligned”`
			} else {
				s = 'Click to toggle SME “isRelatedTo” association<br>'
					+ `${U.meta_or_alt_key_symbol()}-click for “exactMatchOf” / `
					+ `SHIFT-click for “not associated”`
			}
			return s
		},
	},

//////////////////////////////////////////
	watch: {
		resource_level_to_show() {
			this.resource_level_to_show_local = this.resource_level_to_show
		},

		resource_level_to_show_local() {
			if (this.dim == 'comp') {
				this.view_comp(this.dim_id)
			}
		},
	},

/////////////////////////////////////////
	async created() {
		vapp.resource_matrix_view = this
		this.resource_level_to_show_local = this.resource_level_to_show

		await this.load_data()

		// a '?edit' in the url turns editing on right away, for users allowed to edit
		if (this.edit_at_start || (this.query && ('edit' in this.query))) {
			if (this.user_has_edit_rights) {
				this.simscores_showing = true
				this.job_stats_showing = true
				this.editing_enabled = true
			}
		}
	},
	mounted() {
	},

/////////////////////////////////////////
	methods: {
		rco(cell) {
			for (let rc of this.recommendation_categories) {
				if (cell.simscore >= rc.threshold) return rc
			}
			return this.recommendation_categories[this.recommendation_categories.length-1]

			// // recommendation_categories[0] corresponds to alignment_category 6
			// // recommendation_categories[5] corresponds to alignment_category 1
			// return this.recommendation_categories[this.recommendation_categories.length - cell.alignment_category]
		},

		item_description(cfi) {
			// pass back summaryForAlignment if we have it
			if (!empty(cfi.extensions?.summaryForAlignment)) {
				return cfi.extensions.summaryForAlignment
			
			// else description if we have it
			} else if (!empty(cfi.extensions?.description)) {
				return cfi.extensions.description
			}
			return ''
		},

		sme_icon_class(cell) {
			let s = ''

			if (this.cell_just_changed && this.cell_just_changed == cell) {
				s += ' k-rcram-alignment-just-changed'
			}

			if (this.editing_enabled) {
				s += ' k-rcram-simscore-cell-editing'
			}

			// alignment job
			if (this.align_job) {
				// if marked as not_aligned, use this class
				if (cell?.not_aligned == 1) {
					s += ' k-rcram-simscore-sme-not-aligned'

				// else if marked with sme_alignment, use this class
				} else if (cell?.sme_alignment) {
					s += ' k-rcram-simscore-sme-tagged k-rcram-simscore-sme-tagged-' + cell.sme_alignment
				}

			// crosswalk job
			} else {
				// if marked as not_associated, use this class
				if (cell?.not_associated == 1) {
					s += ' k-rcram-simscore-sme-not-associated'

				// else if marked with sme_assoc, use this class
				} else if (cell?.sme_assoc) {
					s += ' k-rcram-simscore-sme-tagged-crosswalk k-rcram-simscore-sme-tagged-crosswalk-' + cell.sme_assoc
				}
			}
			return s
		},

		grid_table_cell_class(comp_id, resource_id, cell) {
			let s = this.sme_icon_class(cell)

			// TODO: highlight last-viewed item...

			if (this.last_viewed_item?.dim == 'resource') {
				if (resource_id == this.last_viewed_item.id) {
					s += ' k-rcram-sorted-resource-cell'
				}
				
			} else if (this.last_viewed_item?.dim == 'comp') {
				if (comp_id == this.last_viewed_item.id) {
					s += ' k-rcram-sorted-comp-cell'
				}
			}

			return s
		},

		calculate_alignment_category(simscore) {
			for (let rc of this.alignment_categories) {
				if (simscore >= rc.threshold) return rc.alignment_category
			}
			return this.alignment_categories.length-1
		},

		calculate_matrix_alignment_categories(matrix) {
			// calculate and replace the alignment_category values for all items in the matrix
			for (let comp_id in matrix) {
				for (let resource_id in matrix[comp_id]) {
					let cell = matrix[comp_id][resource_id]
					cell.alignment_category = this.calculate_alignment_category(cell.simscore)
				}
			}
		},

		async cell_clicked(cell, $event) {
			if (!this.editing_enabled) return

			// set resource_id and comp_id depending on align_job
			let resource_id, comp_id
			if (this.align_job) {
				resource_id = cell.resource_id
				comp_id = cell.competency_id
			} else {
				resource_id = cell.competency_a_id
				comp_id = cell.competency_b_id
			}

			// cell here should be an actual matrix cell, not a grid_table_rows item or some other construct

			// sanity check: make sure this cell matches an item in the matrix or submatrix indexed by resource_id and comp_id
			let cell_check = (this.matrix?.[comp_id]?.[resource_id]) ?? this.sub_matrix?.[comp_id]?.[resource_id]
			if (!cell_check || (cell_check != cell)) {
				this.$alert(`invalid call to cell_clicked: comp_id (${comp_id}) and/or resource_id (${resource_id}) don't match provided cell`)
				return
			}

			let msg

			// ------------------------
			// align_job: sme_alignment
			if (this.align_job) {
				// console.log('\nwas ' + cell.sme_alignment + ' - ' + cell.not_aligned)

				// determine the new values of sme_alignment and not_aligned
				let new_sme_alignment, new_not_aligned

				// if shift key is down, toggle not_aligned
				if ($event.shiftKey) {
					if (cell.not_aligned == 1) {
						new_not_aligned = 0
						new_sme_alignment = null
						msg = 'Cleared “not aligned” flag'
					} else {
						new_not_aligned = 1
						new_sme_alignment = null
						msg = 'Set to <b>not aligned</b>'
					}

				// if meta/alt key is down, toggle 'primary'
				} else if (U.meta_or_alt_key($event)) {
					if (cell.sme_alignment == 'primary') {
						new_not_aligned = 0
						new_sme_alignment = null
						msg = 'Cleared SME alignment'
					} else {
						new_not_aligned = 0
						new_sme_alignment = 'primary'
						msg = 'Set to <b>primary SME alignment</b>'
					}

				// neither shift nor meta/alt down; toggle 'manual'
				} else {
					if (cell.sme_alignment == 'manual') {
						new_not_aligned = 0
						new_sme_alignment = null
						msg = 'Cleared SME alignment'
					} else {
						new_not_aligned = 0
						new_sme_alignment = 'manual'
						msg = 'Set to <b>manual SME alignment</b>'
					}
				}
				// console.warn('now ' + cell.sme_alignment + ' - ' + cell.not_aligned)

				// for service call, sme_alignment gets *CLEAR* to clear (set to null), or the new_sme_alignment value
				let payload = {
					action: 'import_resource_metadata',
					user_id: this.user_info.user_id,
					data: [{
						resource_id: resource_id,
						competency_id: comp_id,
					}],
				}
				if (new_sme_alignment == null) payload.data[0].sme_alignment = '*CLEAR*'
				else payload.data[0].sme_alignment = new_sme_alignment
				payload.data[0].not_aligned = new_not_aligned

				// save the alignment to the DB
				let result = await U.ajax_promise('align/scram', payload)

				if (result.status != 'ok') {
					this.$alert(`ERROR: ${result.status}`)
					return
				}

				// update the cell locally
				cell.sme_alignment = new_sme_alignment
				cell.not_aligned = new_not_aligned

			// ------------------------
			// crosswalk job: sme_assoc
			} else {
				// console.log('\nwas ' + cell.sme_assoc + ' - ' + cell.not_associated)

				// determine the new values of sme_assoc and not_associated
				let new_sme_assoc, new_not_associated

				// if shift key is down, toggle not_associated
				if ($event.shiftKey) {
					if (cell.not_associated == 1) {
						new_not_associated = 0
						new_sme_assoc = null
						msg = 'Cleared “not associated” flag'
					} else {
						new_not_associated = 1
						new_sme_assoc = null
						msg = 'Set to <b>not associated</b>'
					}

				// if meta/alt key is down, toggle 'exactMatchOf'
				} else if (U.meta_or_alt_key($event)) {
					if (cell.sme_assoc == 'exactMatchOf') {
						new_not_associated = 0
						new_sme_assoc = null
						msg = 'Cleared SME alignment'
					} else {
						new_not_associated = 0
						new_sme_assoc = 'exactMatchOf'
						msg = 'Set to <b>exactMatchOf</b> SME association'
					}

				// neither shift nor meta/alt down; toggle 'isRelatedTo'
				} else {
					if (cell.sme_assoc == 'isRelatedTo') {
						new_not_associated = 0
						new_sme_assoc = null
						msg = 'Cleared SME association'
					} else {
						new_not_associated = 0
						new_sme_assoc = 'isRelatedTo'
						msg = 'Set to <b>isRelatedTo</b> SME association'
					}
				}
				// console.warn('now ' + cell.sme_assoc + ' - ' + cell.not_associated)

				// for service call, sme_assoc gets *CLEAR* to clear (set to null), or the new_sme_assoc value
				let payload = {
					action: 'import_crosswalk_metadata',
					user_id: this.user_info.user_id,
					data: [{
						competency_a_id: resource_id,
						competency_b_id: comp_id,
					}],
				}
				if (new_sme_assoc == null) payload.data[0].sme_assoc = '*CLEAR*'
				else payload.data[0].sme_assoc = new_sme_assoc
				payload.data[0].not_associated = new_not_associated

				console.warn(payload)

				// save the assoc to the DB
				let result = await U.ajax_promise('align/scram', payload)

				if (result.status != 'ok') {
					this.$alert(`ERROR: ${result.status}`)
					return
				}

				// update the cell locally
				cell.sme_assoc = new_sme_assoc
				cell.not_associated = new_not_associated
			}

			// make the icon blink
			clearTimeout(this.cell_just_changed_timeout)
			this.cell_just_changed = cell
			this.cell_just_changed_timeout = setTimeout(x=>{ this.cell_just_changed = null }, 1500)

			this.$inform(msg)

			// briefly hide all tooltips; if we don't do this and the clicked item has changed position,
			// the tooltip can get "stuck"
			this.hide_tooltips = true
			setTimeout(x=>{ 
				$('.v-tooltip__content').css('display', 'none')
				this.hide_tooltips = false 
			}, 100)
		},

		view_grid() {
			if (this.opened_from_vapp) {
				vapp.show_resource_matrix_view({
					cram_job_id: this.cram_job_id+'',
					dim: 'grid',
				})
			} else {
				this.$router.push({ path: `/crm/${this.cram_job_id}/grid/all/${this.resource_level_to_show_local}` })
			}
			this.n_showing = 0
		},

		view_row_item(id) {
			if (this.grid_mode == 'axhigh') {
				this.view_resource(id)
			} else {
				this.view_comp(id)
			}
		},

		view_comp(id) {
			this.last_viewed_item = { dim: 'comp', id: id }
			if (this.opened_from_vapp) {
				vapp.show_resource_matrix_view({
					cram_job_id: this.cram_job_id+'',
					dim: 'comp',
					dim_id: id,
				})
			} else {
				this.$router.push({ path: `/crm/${this.cram_job_id}/comp/${id}/${this.resource_level_to_show_local}` })
			}
			this.n_showing = 0
		},

		view_resource(id) {
			this.last_viewed_item = { dim: 'resource', id: id }
			if (this.opened_from_vapp) {
				vapp.show_resource_matrix_view({
					cram_job_id: this.cram_job_id+'',
					dim: 'resource',
					dim_id: id,
				})
			} else {
				this.$router.push({ path: `/crm/${this.cram_job_id}/resource/${id}/${this.resource_level_to_show_local}` })
			}
			this.n_showing = 0
		},

		edit_resources() {
			this.editing_enabled = true
			this.view_resource(this.resource_ids[0])
		},

		edit_comps() {
			this.editing_enabled = true
			this.view_comp(this.comp_ids[0])
		},

		show_more() {
			this.n_showing += this.SHOW_MORE_BLOCK_SIZE
		},

		toggle_components_for(id, val) {
			let o = structuredClone(this.components_showing)
			if (empty(val)) val = !o[id]
			o[id] = val
			this.components_showing = o
		},

		toggle_description(id, val) {
			let o = structuredClone(this.descriptions_showing)
			if (empty(val)) val = !o[id]
			o[id] = val
			this.descriptions_showing = o
		},
	}
}
</script>

<style lang="scss">
.k-rcram-title {
	border-bottom:1px solid transparent;
	background-color:#eee;
	padding:0 24px 0 70px!important;
	height:60px;
}

.k-rcram-report-title {
	margin-left:8px;
}

.k-rcram-logo {
	position:fixed;
	left:0px;
	top:0px;
	height:60px;
}

.k-rcram-body {
	padding:12px!important;
	font-size:16px;
}

.k-rcram-h1 {
	display:flex;
	font-size:20px;
	line-height:24px;
	margin:0 4px 6px 4px;
}
.k-rcram-h2 {
	font-size:18px;
	line-height:21px;
	margin-bottom:12px;
}

.k-rcram-table-outer {
	overflow:scroll;
	border-radius:8px;
	border:1px solid #000;

	// this makes rendering marked latex work better
	p { margin:0!important; }
}

.k-rcram-table {
	max-width:none!important;

	table {
		// without this the table is squeezed to 100%
		width:max-content!important;
		table-layout: fixed;
		border-top:1px solid #ccc;
		border-bottom:1px solid #000;
		border-right:1px solid #000!important;
	}
}

/* Make all header cells sticky at the top */
.k-rcram-table th {
	position: sticky;
	top: 0;
	z-index: 2;
	background-color: var(--v-background-base, #fff);
}

/* Pin the first column body cells to the left */
.k-rcram-table td.k-rcram-table-comp-header-cell {
	position: sticky;
	left: 0;
	z-index: 3;
	background-color: var(--v-background-base, #fff);
}

/* Pin top-left intersection cell (First Column Header) */
.k-rcram-table th.k-rcram-table-comp-header-cell {
	position: sticky;
	top: 0;
	left: 0;
	z-index: 4;
	background-color: var(--v-background-base, #fff);
}

.k-rcram-table-header {
	th {
		padding: 4px 6px !important;
		height: 12px !important;
		font-size: 12px !important;
		line-height:14px!important;
		font-weight: normal;
		// cursor: pointer;
		color:#000!important;
		border-left:1px solid #ccc;
		vertical-align:top;
		// background-color:#ddd;
	}
}

.k-rcram-table-comp-header-cell {
	// width:300px!important;
	font-size:12px!important;
	line-height:15px;
	// background-color:#ddd;
	padding:6px 40px 6px 6px!important;
	border-left:1px solid #ccc;
	border-right:1px solid #ccc;
	position:relative;
	border-right:1px solid #000!important;

	// this makes rendering marked latex work better
	p { margin:0!important; }

	.v-btn { opacity:0.3; }
}
.k-rcram-table-comp-header-cell:hover {
	.v-btn { opacity:1; }
}

th.k-rcram-table-comp-header-cell {
	vertical-align:bottom!important;
	border-bottom:1px solid #000!important;
}

.k-rcram-comp-header-btns {
	position:absolute;
	right:0px;
	top:0;
	display:flex;
	flex-direction:column;
	justify-content: center;
	align-items: center;
	padding-right:4px;
	height:100%;

	.v-btn {
		min-width:32px!important;
		padding:0!important;
	}
}

.k-rcram-table-resource-header-cell {
	font-size:12px!important;
	line-height:15px;
	border-left:1px solid #ccc;
	border-top:1px solid #ccc;
	border-bottom:1px solid #000!important;
	text-align:center;
	position:relative;
	.v-btn { opacity:0.3; }
}
.k-rcram-table-resource-header-cell:hover {
	.v-btn { opacity:1; }
}

.k-rcram-table-resource-header-cell.k-rcram-sorted-resource-cell {
	border-top:2px solid #000!important;
}

.k-rcram-resource-header-div {
	padding-bottom:26px!important;
}

.k-rcram-table-bxhigh, .k-rcram-table-axhigh {
	.k-rcram-table-resource-header-cell {
		font-size:18px!important;
		// font-weight:bold;
		.k-rcram-resource-header-div {
			padding-top:8px!important;
			padding-bottom:8px!important;
		}
	}
}

.k-rcram-resource-header-btns {
	position:absolute;
	bottom:0;
	left:0;
	width:100%;
	display:flex;
	justify-content: center;
	padding-bottom:6px;
	.v-btn {
		min-width:32px!important;
		padding:0!important;
	}
}

.k-rcram-table-simscore-cell {
	// width:100px;
	font-size:12px!important;
	line-height:15px;
	border-left:1px solid #ccc;
	text-align:center;
}

.k-rcram-simscore-cell-editing {
	cursor:pointer;
}

@keyframes rcram-alignment-just-changed-blink {
  0%   { opacity: 0.1; }
  50%  { opacity: 1; }
  100% { opacity: 0.1; }
}

.k-rcram-alignment-just-changed {
	.v-icon {
		animation: rcram-alignment-just-changed-blink 500ms step-end infinite;
	}
}

// it's imported that the `-sorted-` selectors appear above the `-simscore-` selectors so that the background-colors cascade poperly
.k-rcram-sorted-resource-cell {
	border-left:2px solid #000!important;
	border-right:2px solid #000!important;
	font-weight:bold!important;
	background-color: #ccc!important;
	.v-btn { opacity:1; }
}
.k-rcram-sorted-comp-cell {
	border-top:2px solid #000!important;
	border-bottom:2px solid #000!important;
	font-weight:bold!important;
	background-color: #ccc!important;
	.v-btn { opacity:1; }
}

.k-rcram-table-simscore-cell-4 {   
	background-color:$v-green-lighten-3!important;
	.v-icon { color: $v-green-darken-3; }
}
.k-rcram-table-simscore-cell-3 {   
	background-color:$v-blue-lighten-4!important;
	.v-icon { color: $v-blue-darken-3; }
}
.k-rcram-table-simscore-cell-2 {   
	background-color:$v-purple-lighten-5!important;
	.v-icon { color: $v-purple-darken-3; }
}
.k-rcram-table-simscore-cell-1 {   
	background-color:$v-grey-lighten-3!important;
	.v-icon { color: $v-grey-darken-4; font-size:12px!important;}
}
.k-rcram-table-simscore-cell-0 {   
	background-color:#fff!important;
	.v-icon { color: #666; font-size:12px!important;}
}

// the following two have to go below the x-cell-x styles
.k-rcram-simscore-sme-tagged {
	.v-icon {
		width:30px!important;
		height:30px!important;
		padding:0px!important;
		border: 3px solid $v-amber-accent-3;
		border-radius:32px;
		font-size:24px!important;
		color: $v-green-darken-4!important;
		font-weight:900!important;
	}
	.v-icon::before {
		// content: "\e123"!important;	// circle-star
	}
}
.k-rcram-key-entry {
	.k-rcram-simscore-sme-tagged, .k-rcram-simscore-sme-tagged-primary {
		.v-icon {
			width:24px!important;
			height:24px!important;
			font-size:18px!important;
			margin-top:-2px;
		}
	}
}

.k-rcram-simscore-sme-tagged-primary {
	.v-icon {
		width:30px!important;
		height:30px!important;
		padding:0px!important;
		border: 3px solid $v-amber-accent-3;
		border-radius:32px;
		font-size:24px!important;
		color: $v-green-darken-4!important;
		font-weight:900!important;
	}
	.v-icon::before {
		content: "\e123"!important;	// circle-star
	}
}

.k-rcram-simscore-sme-not-aligned {
	.v-icon {
		// width:30px!important;
		// height:30px!important;
		// padding:0px!important;
		// border: 3px solid $v-red-darken-4;
		// border-radius:32px;
		font-size:24px!important;
		color: $v-red-darken-4!important;
		// font-weight:900!important;
	}
	.v-icon::before {
		content: "\f05e"!important;	// ban
	}
}
.k-rcram-key-entry .k-rcram-simscore-sme-not-aligned {
	.v-icon {
		// width:24px!important;
		// height:24px!important;
		font-size:18px!important;
		margin-top:-2px;
	}
}

//////////////// crosswalks
.k-rcram-simscore-sme-tagged-crosswalk {
	.v-icon {
		width:30px!important;
		height:30px!important;
		padding:0px!important;
		border: 3px solid $v-amber-accent-3;
		border-radius:32px;
		font-size:24px!important;
		color: $v-green-darken-4!important;
		font-weight:900!important;
	}
}

.k-rcram-simscore-sme-tagged-crosswalk-exactMatchOf {
	.v-icon::before {
		content: "\f111"!important;	// filled circle
	}
}
.k-rcram-simscore-sme-tagged-crosswalk-isRelatedTo {
	.v-icon::before {
		content: "\f042"!important;	// circle-half-stroke
	}
}

.k-rcram-simscore-sme-not-associated {
	.v-icon {
		// width:30px!important;
		// height:30px!important;
		// padding:0px!important;
		// border: 3px solid $v-red-darken-4;
		// border-radius:32px;
		font-size:24px!important;
		color: $v-red-darken-4!important;
		// font-weight:900!important;
	}
	.v-icon::before {
		content: "\f05e"!important;	// ban
	}
}
.k-rcram-key-entry .k-rcram-simscore-sme-not-associated {
	.v-icon {
		// width:24px!important;
		// height:24px!important;
		font-size:18px!important;
		margin-top:-2px;
	}
}

////////////////////////
.k-rcram-list-header {
	font-size:18px;
	line-height:23px;
	border-bottom:1px solid #000;
	padding-bottom:8px;
}

.k-cram-list-item-outer {
	border-radius:5px;
	border:1px solid #333;
	margin-bottom:8px;
}

.k-cram-list-item-header {
	padding:8px 8px 8px 0;
	// border-radius: 4px 4px 0 0;
	border-radius: 4px;
	font-size:16px;
	line-height:21px;
}

.k-cram-list-cat-icon {
	flex: 0 0 48px;
	text-align:center;
}

.k-cram-list-item-simscore {
	white-space:nowrap;
	flex: 0 0 28px;
	text-align:left;
	font-size:12px;
	font-weight:bold;
	color:#666;
	margin-right:8px;
}

.k-cram-list-item-type {
	background-color:#eee;
	color:#000;
	border-radius:3px;
	padding:0 4px;
	margin-right:12px;
	white-space:nowrap;
	font-size:14px;
}

///////////////////////
.k-rcram-key {
	position:fixed;
	right:8px;
	bottom:8px;
	border:1px solid #000;
	border-radius:6px;
	background-color:#fff;
	padding:4px 0 4px 0;
	.k-rcram-table-simscore-cell-4, .k-rcram-table-simscore-cell-3, .k-rcram-table-simscore-cell-2 {   
		.v-icon { margin-top:-2px }
	}
	.k-rcram-table-simscore-cell-1, .k-rcram-table-simscore-cell-0 {   
		.v-icon { margin-top:-3px }
	}
}

.k-rcram-key-entry {
	padding:2px 8px 2px 2px;
}

.k-rcram-key-btn {
	position:fixed;
	right:8px;
	bottom:8px;
}

.k-cram-resource-description {
	width:760px;
	font-size:14px;
	line-height:18px;
}

//////////////////////
// k-rcram-table-outer
.k-rcram-report-header {
	font-size:17px;
	line-height:22px;
	padding:12px;
	max-width:800px;
	margin-left:auto;
	margin-right:auto;
	background-color:#eee;
	border-radius:12px;
}

.k-rcram-report-filters {
	display:flex;
}

.k-rcram-report-table {
	th {
		// this makes the headers sticky
		position: sticky;
		top: 0;
		z-index: 2;

		background-color:$v-indigo-darken-4;
		color:#fff!important;
		padding:8px 16px!important;
		font-size:14px!important;
		line-height:18px;
		border-left:1px solid #fff!important;
	}
	th:first-of-type {
		border-left:0!important;
	}
}

.k-rcram-comp-cell {
	font-size:16px;
	line-height:21px;
}

.k-cram-report-ancestor-comp {
	background-color:$v-indigo-lighten-5!important;
	border-top:3px solid $v-indigo-darken-3!important;
	color:#000!important;
	line-height:20px;
	padding-top:4px!important;
	padding-bottom:4px!important;
	font-size:14px!important;
	line-height:17px!important;

	p {
		text-align:center;
	}

	.k-cram-report-ancestor-comp-component {
		padding-left:8px;
	}
	.k-cram-report-ancestor-comp-component:before {
		content:"\f178";	// arrow-right-long
		font-family:'Font Awesome 6 Pro';
		margin-right:8px;
	}
}

.k-rcram-addressed-cell {
	text-align:left;
	width:164px;
	font-size:14px!important;
	white-space:nowrap;
	padding-left:8px!important;
	padding-right:8px!important;
}
.k-rcram-addressed-cell-div {
	padding:8px 8px 8px 4px;
}
.k-rcram-addressed-cell-icon {
	margin-right:4px;
}

.k-rcram-addressed-branch-cell {
	text-align:center;
	width:120px;
}

th.k-rcram-addressed-branch-cell {
	font-size:12px!important;
	line-height:14px!important;
}

.k-rcram-citations-cell {
	p {
		margin:4px 0 4px 32px!important;
		text-indent: -32px;
		font-size:14px;
		line-height:17px;
	}
	p:hover {
		text-decoration:underline;
		cursor:pointer;
	}
}

.k-rcram-citation-detail-title p {
	font-size: 17px;
	line-height:20px;
	padding-bottom:12px;
	margin-bottom:12px;
	border-bottom:1px solid #000;
}

.k-rcram-citation-detail-text {

}

.k-rcram-show-all-citations-btn {
	margin-right:-8px;
}

.k-rcram-all-citations-list {
	p {
		margin-bottom:8px;
	}
	p:hover {
		text-decoration:underline;
		cursor:pointer;
	}
}

.k-rcram-resource-branch-title {
	color:#fff;
	border-radius:4px;
	padding:4px 8px;
	margin-right:8px;
	margin-left:8px;
	p { margin:0; }
}

.k-rcram-bold-label {
	font-size:20px;
}

.k-rcram-alignment-tier-summary-element {
	white-space:nowrap;
	font-size:18px!important;
	margin-left:20px;
}

////////////////////////////////////////////////////////////
// PRINT VIEW CSS
.k-rcram-report-a-dialog-print {
	position: static!important;
    overflow: visible!important;
	box-shadow: none!important;

	a {
		color:#000!important;
	}

	.k-rcram-report-a-card {
		box-shadow:none!important;
	}

	.k-rcram-title {
		padding:0!important;
	}

	.k-rcram-report-title {
		margin-left:12px;
		font-size:16px;
	}

	.k-rcram-logo {
		position:static;
	}

	.k-rcram-print-btn {
		display:none!important;
	}

	.k-rcram-body {
		padding:12px 0 0 0!important;
	}

	.k-rcram-h1 {
		display:block;
		font-size:16px;
		.spacer { display:none; }
	}

	.k-rcram-bold-label {
		font-size:14px;
		line-height:18px;
	}

	.k-rcram-alignment-tier-summary-element {
		white-space:nowrap;
		font-size:12px!important;
	}

	.k-rcram-report-header {
		font-size:16px;
		line-height:20px;
		background-color:#fff!important;
		padding:12px 0;
		border-top:1px solid #666;
		border-bottom:1px solid #666;
		border-radius:0;
	}

	.k-rcram-report-filters {
		display:none;
	}

	.k-rcram-resource-branch-title {
		padding:0;
		margin-right:8px;
		margin-left:8px;
	}

	.k-rcram-table-outer {
		overflow: visible;
		border-radius:0;
		border:0!important;
		box-shadow:none!important;
		margin-top:24px;
		margin:24px 0;
	}

	/* Remove overflow constraints on table wrappers (essential for Vuetify v-data-table) */
	.v-data-table, .v-data-table__wrapper {
		overflow: visible !important;
		height: auto !important;
	}

	.k-rcram-report-table {
		border-radius:0!important;
		border-bottom:1px solid #000!important;
		tr {
			break-inside: avoid !important;
			page-break-inside: avoid !important;
		}

		td, th {
			break-inside: avoid !important;
			page-break-inside: avoid !important;
		}

		tr > th:last-child, tr > td:last-child {
			border-right: 1px solid #000;
		}
		tr > th:first-child, tr > td:first-child {
			border-left: 1px solid #000!important;
		}

		th {
			// no sticky headers
			position: static;
			font-size:14px!important;
			line-height:15px!important;
			padding:4px 8px!important;
			background-color:#fff!important;
			color:#000!important;
			border-top:1px solid #000!important;
			border-left:1px solid #000!important;
			border-bottom:1px solid #000!important;
		}
	}

	/* Prevent page breaks directly after rows with this class */
	tr.k-rcram-ancestor-row {
		break-after: avoid !important;
		page-break-after: avoid !important;
		break-inside: avoid !important;
		page-break-inside: avoid !important;
	}

	.k-cram-report-ancestor-comp {
		font-size:12px!important;
		line-height:15px!important;
		border-top:2px solid #000!important;
	}

	.k-rcram-comp-cell {
		font-size:13px!important;
		line-height:16px;
		padding:0 8px !important;
	}

	.k-cram-report-ancestor-comp-component {
		padding-left:6px;
	}
	.k-cram-report-ancestor-comp-component:before {
		margin-right:6px;
	}

	.k-rcram-addressed-cell {
		text-align:center;
		width:100px;
	}

	.k-rcram-addressed-cell-div {
		padding:0;
	}
	.k-rcram-addressed-cell-icon {
		margin:0;
	}
	.k-rcram-addressed-cell-label {
		display:none;
	}

	.k-rcram-addressed-branch-cell {
		width:80px;
	}

	th.k-rcram-addressed-branch-cell {
		font-size:10px!important;
		line-height:12px!important;
	}
	th.k-rcram-addressed-cell-header {
		font-size:10px!important;
		line-height:12px!important;
	}

	.k-rcram-citations-cell {
		padding:0 8px!important;
		p {
			margin:4px 0 4px 20px!important;
			text-indent: -20px;
			font-size:11px!important;
			line-height:13px!important;
		}
	}

	.k-rcram-show-all-citations-btn {
		display:none;
	}
}

.k-rcram-report-a-dialog-parent-print {
	position: static!important;
	box-shadow: none!important;
}
</style>